import moment from 'moment';
import type { UploadsSummary } from '@/models/upload';
import styles from './UploadsSummaryCards.module.css';

interface UploadsSummaryCardsProps {
  summary?: UploadsSummary;
  loading?: boolean;
}

const formatRelative = (iso: string | null): string => {
  if (!iso) return '—';
  const m = moment(iso);
  if (!m.isValid()) return '—';
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  if (m.isSameOrAfter(today)) return `Today, ${m.format('h:mm A')}`;
  if (m.isSameOrAfter(yesterday)) return `Yesterday, ${m.format('h:mm A')}`;
  return m.format('MMM D, YYYY');
};

const studyWord = (n: number) => (n === 1 ? 'study' : 'studies');

export default function UploadsSummaryCards({
  summary,
  loading,
}: UploadsSummaryCardsProps) {
  const lastUploadAt = summary?.lastUpload?.at ?? null;
  const lastUploadCount = summary?.lastUpload?.studyCount ?? 0;
  const studiesUploadedCount = summary?.studiesUploadedCount ?? 0;
  const windowDays = summary?.studiesUploadedWindowDays ?? 7;
  const inProcessing = summary?.inProcessingCount ?? 0;
  const failed = summary?.failedCount ?? 0;

  return (
    <div className={styles.grid} aria-busy={loading}>
      <article className={styles.card}>
        <div className={styles.label}>Last upload</div>
        <div className={styles.value}>{formatRelative(lastUploadAt)}</div>
        <div className={styles.subText}>
          {lastUploadCount > 0
            ? `${lastUploadCount} ${studyWord(lastUploadCount)}`
            : 'No uploads yet'}
        </div>
      </article>

      <article className={styles.card}>
        <div className={styles.label}>Studies uploaded</div>
        <div className={styles.value}>{studiesUploadedCount}</div>
        <div className={styles.subText}>in the last {windowDays} days</div>
      </article>

      <article className={`${styles.card} ${styles.info}`}>
        <div className={styles.label}>In processing</div>
        <div className={styles.value}>
          {inProcessing} {studyWord(inProcessing)}
        </div>
        <div className={styles.subText}>in progress</div>
      </article>

      <article className={`${styles.card} ${styles.danger}`}>
        <div className={styles.label}>Failed studies</div>
        <div className={styles.value}>
          {failed} {studyWord(failed)}
        </div>
        <div className={styles.subText}>need attention</div>
      </article>
    </div>
  );
}
